import React, { memo, useMemo, useRef, useState } from "react";
import {
  EdgeProps,
  getSmoothStepPath,
  useReactFlow,
  useStore,
} from "@xyflow/react";

type Rect = { x: number; y: number; w: number; h: number };

const PAD = 18;
const DETOUR_GAP = 32;
const BEND_HANDLE_R = 7;

type Point = { x: number; y: number };

const expandRect = (r: Rect, pad: number): Rect => ({
  x: r.x - pad,
  y: r.y - pad,
  w: r.w + pad * 2,
  h: r.h + pad * 2,
});

const segmentIntersectsRect = (
  a: { x: number; y: number },
  b: { x: number; y: number },
  r: Rect
): boolean => {
  // Only handle axis-aligned segments. If diagonal, treat as intersecting.
  const ax = a.x;
  const ay = a.y;
  const bx = b.x;
  const by = b.y;

  const left = r.x;
  const right = r.x + r.w;
  const top = r.y;
  const bottom = r.y + r.h;

  if (ax === bx) {
    // vertical
    const x = ax;
    const y1 = Math.min(ay, by);
    const y2 = Math.max(ay, by);
    if (x < left || x > right) return false;
    return !(y2 < top || y1 > bottom);
  }

  if (ay === by) {
    // horizontal
    const y = ay;
    const x1 = Math.min(ax, bx);
    const x2 = Math.max(ax, bx);
    if (y < top || y > bottom) return false;
    return !(x2 < left || x1 > right);
  }

  return true;
};

const pathIntersectsAny = (
  points: Array<{ x: number; y: number }>,
  rects: Rect[]
) => {
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    for (const r of rects) {
      if (segmentIntersectsRect(a, b, r)) return true;
    }
  }
  return false;
};

const pointsToPath = (points: Array<{ x: number; y: number }>) => {
  if (points.length < 2) return "";
  return (
    `M ${points[0].x},${points[0].y} ` +
    points
      .slice(1)
      .map((p) => `L ${p.x},${p.y}`)
      .join(" ")
  );
};

function SmartEdge(props: EdgeProps) {
  const nodeLookup = useStore((s) => s.nodeLookup);
  const { screenToFlowPosition } = useReactFlow();
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    idx: number;
    start: Point;
    startScreen: { x: number; y: number };
    points: Point[]; // intermediate points only
    autoPoints: Point[]; // intermediate points only
  } | null>(null);

  const obstacleRects = useMemo(() => {
    const rects: Rect[] = [];
    nodeLookup.forEach((n: any) => {
      if (!n) return;
      if (n.id === props.source || n.id === props.target) return;
      const type = n.type as string | undefined;
      if (type === "junction") return;

      const w = Number(n.measured?.width ?? n.width ?? 0);
      const h = Number(n.measured?.height ?? n.height ?? 0);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0)
        return;

      const x = Number(
        n.internals?.positionAbsolute?.x ?? n.positionAbsolute?.x ?? 0
      );
      const y = Number(
        n.internals?.positionAbsolute?.y ?? n.positionAbsolute?.y ?? 0
      );
      rects.push(expandRect({ x, y, w, h }, PAD));
    });
    return rects;
  }, [nodeLookup, props.source, props.target]);

  const { sourceX, sourceY, targetX, targetY } = props;

  const computed = useMemo(() => {
    const sx = sourceX;
    const sy = sourceY;
    const tx = targetX;
    const ty = targetY;

    const manual = (props.data as any)?.manualPoints as Point[] | undefined;
    if (Array.isArray(manual) && manual.length > 0) {
      const pts = [{ x: sx, y: sy }, ...manual, { x: tx, y: ty }];
      return { kind: "manual" as const, points: pts, path: pointsToPath(pts) };
    }

    // Try a small set of orthogonal candidates that often avoid collisions.
    const midX = (sx + tx) / 2;
    const midY = (sy + ty) / 2;

    const candidates: Array<Array<{ x: number; y: number }>> = [
      // horizontal then vertical
      [
        { x: sx, y: sy },
        { x: midX, y: sy },
        { x: midX, y: ty },
        { x: tx, y: ty },
      ],
      // vertical then horizontal
      [
        { x: sx, y: sy },
        { x: sx, y: midY },
        { x: tx, y: midY },
        { x: tx, y: ty },
      ],
    ];

    // Detour X to the left/right of the obstacle cluster.
    if (obstacleRects.length > 0) {
      const minX = Math.min(...obstacleRects.map((r) => r.x));
      const maxX = Math.max(...obstacleRects.map((r) => r.x + r.w));
      const detourLeft = minX - DETOUR_GAP;
      const detourRight = maxX + DETOUR_GAP;
      candidates.push(
        [
          { x: sx, y: sy },
          { x: detourLeft, y: sy },
          { x: detourLeft, y: ty },
          { x: tx, y: ty },
        ],
        [
          { x: sx, y: sy },
          { x: detourRight, y: sy },
          { x: detourRight, y: ty },
          { x: tx, y: ty },
        ]
      );

      // Detour Y above/below the obstacle cluster.
      const minY = Math.min(...obstacleRects.map((r) => r.y));
      const maxY = Math.max(...obstacleRects.map((r) => r.y + r.h));
      const detourUp = minY - DETOUR_GAP;
      const detourDown = maxY + DETOUR_GAP;
      candidates.push(
        [
          { x: sx, y: sy },
          { x: sx, y: detourUp },
          { x: tx, y: detourUp },
          { x: tx, y: ty },
        ],
        [
          { x: sx, y: sy },
          { x: sx, y: detourDown },
          { x: tx, y: detourDown },
          { x: tx, y: ty },
        ]
      );
    }

    for (const pts of candidates) {
      if (!pathIntersectsAny(pts, obstacleRects)) {
        return {
          kind: "auto" as const,
          points: pts,
          path: pointsToPath(pts),
        };
      }
    }

    // Fall back to smoothstep if we couldn't find a clean orthogonal route.
    const [path] = getSmoothStepPath({
      sourceX: sx,
      sourceY: sy,
      targetX: tx,
      targetY: ty,
      sourcePosition: props.sourcePosition,
      targetPosition: props.targetPosition,
      borderRadius: 16,
    });
    return { kind: "smooth" as const, points: [], path };
  }, [
    obstacleRects,
    props.data,
    props.sourcePosition,
    props.targetPosition,
    sourceX,
    sourceY,
    targetX,
    targetY,
  ]);

  const intermediatePoints = useMemo(() => {
    if (computed.kind === "smooth") return [];
    // Computed points always include endpoints for auto/manual.
    const pts = computed.points;
    if (pts.length <= 2) return [];
    return pts.slice(1, -1);
  }, [computed.kind, computed.points]);

  const handlePointerDown = (
    e: React.PointerEvent,
    idx: number,
    currentPoints: Point[]
  ) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);

    const startScreen = { x: e.clientX, y: e.clientY };
    const startFlow = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const start = { x: startFlow.x, y: startFlow.y };

    // Copy current intermediate points so we can promote auto -> manual.
    const points = currentPoints.map((p) => ({ x: p.x, y: p.y }));
    const autoPoints = currentPoints.map((p) => ({ x: p.x, y: p.y }));

    dragRef.current = {
      idx,
      start,
      startScreen,
      points,
      autoPoints,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const ref = dragRef.current;
    const curFlow = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const dx = curFlow.x - ref.start.x;
    const dy = curFlow.y - ref.start.y;

    // Decide axis with a guard to avoid breaking the endpoint-adjacent segment.
    const idx = ref.idx;
    const p = ref.autoPoints[idx];
    const prev: Point =
      idx === 0 ? { x: sourceX, y: sourceY } : ref.autoPoints[idx - 1];
    const next: Point =
      idx === ref.autoPoints.length - 1
        ? { x: targetX, y: targetY }
        : ref.autoPoints[idx + 1];

    const prevIsEndpoint = idx === 0;
    const nextIsEndpoint = idx === ref.autoPoints.length - 1;

    const canMoveX = true;
    const canMoveY = true;

    const prevHorizontal = prev.y === p.y;
    const prevVertical = prev.x === p.x;
    const nextHorizontal = p.y === next.y;
    const nextVertical = p.x === next.x;

    // Keep the bend orthogonal to avoid diagonal movement while dragging corners.
    // For right-angle corners, move only along the axis that maintains the two
    // adjacent axis-aligned segments.
    let axisLockedX = canMoveX;
    let axisLockedY = canMoveY;

    if (canMoveX && canMoveY) {
      if (prevHorizontal && nextVertical) {
        if (prevIsEndpoint && Math.abs(dy) > Math.abs(dx)) {
          axisLockedY = true;
          axisLockedX = false;
        } else {
          axisLockedY = false;
        }
      } else if (prevVertical && nextHorizontal) {
        if (prevIsEndpoint && Math.abs(dx) > Math.abs(dy)) {
          axisLockedX = true;
          axisLockedY = false;
        } else {
          axisLockedX = false;
        }
      } else if (prevHorizontal && nextHorizontal) {
        // Collinear horizontal points: preserve a horizontal segment.
        axisLockedY = false;
      } else if (prevVertical && nextVertical) {
        // Collinear vertical points: preserve a vertical segment.
        axisLockedX = false;
      } else if (Math.abs(dx) > Math.abs(dy)) {
        axisLockedY = false;
      } else {
        axisLockedX = false;
      }
    }

    const deltaX = axisLockedX ? dx : 0;
    const deltaY = axisLockedY ? dy : 0;

    const nextPoints = ref.points.map((pt) => ({ ...pt }));
    nextPoints[idx] = {
      ...nextPoints[idx],
      x: ref.autoPoints[idx].x + deltaX,
      y: ref.autoPoints[idx].y + deltaY,
    };

    if (idx > 0 && ref.autoPoints[idx - 1].x === ref.autoPoints[idx].x) {
      nextPoints[idx - 1] = {
        ...nextPoints[idx - 1],
        x: ref.autoPoints[idx - 1].x + deltaX,
      };
    } else if (idx > 0 && ref.autoPoints[idx - 1].y === ref.autoPoints[idx].y) {
      nextPoints[idx - 1] = {
        ...nextPoints[idx - 1],
        y: ref.autoPoints[idx - 1].y + deltaY,
      };
    }

    if (
      idx < ref.autoPoints.length - 1 &&
      ref.autoPoints[idx + 1].x === ref.autoPoints[idx].x
    ) {
      nextPoints[idx + 1] = {
        ...nextPoints[idx + 1],
        x: ref.autoPoints[idx + 1].x + deltaX,
      };
    } else if (
      idx < ref.autoPoints.length - 1 &&
      ref.autoPoints[idx + 1].y === ref.autoPoints[idx].y
    ) {
      nextPoints[idx + 1] = {
        ...nextPoints[idx + 1],
        y: ref.autoPoints[idx + 1].y + deltaY,
      };
    }

    if (
      prevIsEndpoint &&
      prevHorizontal &&
      nextVertical &&
      !axisLockedX &&
      axisLockedY
    ) {
      const y = ref.autoPoints[idx].y + dy;
      const x = sourceX;
      nextPoints[idx] = { x, y };
      if (
        idx < ref.autoPoints.length - 1 &&
        ref.autoPoints[idx + 1].y === ref.autoPoints[idx].y
      ) {
        nextPoints[idx + 1] = {
          ...nextPoints[idx + 1],
          y,
        };
      }
    }

    if (
      prevIsEndpoint &&
      prevVertical &&
      nextHorizontal &&
      !axisLockedY &&
      axisLockedX
    ) {
      const x = ref.autoPoints[idx].x + dx;
      const y = sourceY;
      nextPoints[idx] = { x, y };
      if (
        idx < ref.autoPoints.length - 1 &&
        ref.autoPoints[idx + 1].x === ref.autoPoints[idx].x
      ) {
        nextPoints[idx + 1] = {
          ...nextPoints[idx + 1],
          x,
        };
      }
    }

    ref.points = nextPoints;

    window.dispatchEvent(
      new CustomEvent("mindmap:edgebends", {
        detail: {
          edgeId: props.id,
          manualPoints: nextPoints,
          commit: false,
        },
      })
    );
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    window.dispatchEvent(
      new CustomEvent("mindmap:edgebends", {
        detail: {
          edgeId: props.id,
          manualPoints: dragRef.current.points,
          commit: true,
        },
      })
    );
    dragRef.current = null;
    setIsDragging(false);
  };

  return (
    <g
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <path
        id={props.id}
        className="react-flow__edge-path"
        d={computed.path}
        style={props.style}
        markerEnd={props.markerEnd}
      />
      {props.selected &&
        intermediatePoints.map((p, idx) => (
          <circle
            key={`${props.id}-bend-${idx}`}
            cx={p.x}
            cy={p.y}
            r={BEND_HANDLE_R}
            onPointerDown={(e) => handlePointerDown(e, idx, intermediatePoints)}
            style={{
              cursor: "move",
              fill: "rgba(93, 196, 178, 0.35)",
              stroke: "rgba(93, 196, 178, 0.85)",
              strokeWidth: 2,
              pointerEvents: "all",
              opacity: isDragging ? 0.9 : 0.7,
            }}
          />
        ))}
    </g>
  );
}

export default memo(SmartEdge);
